// src/store/organizationsStore.ts
import { create } from "zustand";
import { db } from "@/lib/firebase";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import type { Organization, OrgNode } from "@/types/organization";
import { useAuthStore } from "./authStore";
import { toast } from "@/hooks/use-toast";

interface OrganizationsState {
  organizations: Organization[];
  currentOrganization: Organization | null;
  loading: boolean;
  error: string | null;

  init: () => void;
  cleanup: () => void;

  createOrganization: (name: string, shortName: string, city?: string) => Promise<string | null>;
  
  /** Создаёт пользователей на основе переданного массива (из структуры) */
  createDefaultUsers: (orgId: string, users?: any[]) => Promise<boolean>;

  updateOrganization: (orgId: string, updates: Partial<Organization>) => Promise<void>;
  deleteOrganization: (orgId: string) => Promise<void>;
  updateStructure: (orgId: string, newStructure: OrgNode[]) => Promise<void>;

  setCurrentOrganization: (org: Organization | null) => void;
}

let unsubscribe: (() => void) | null = null;

// ====================== ШАБЛОН СТРУКТУРЫ ======================
const DEFAULT_STRUCTURE: OrgNode[] = [
  { id: "node-1", name: "Заместитель руководителя", personName: "", numericId: 1, role: "Заместитель руководителя ВСКС", icon: "Crown", order: 1, children: [] },
  { id: "node-2", name: "Начальник отдела",        personName: "", numericId: 2, role: "Начальник ОРВ",                icon: "Bomb",  order: 2, children: [] },
  { id: "node-3", name: "Заместители начальника",  personName: "", numericId: 0, role: "Заместители начальника ОРВ",    icon: "Users", order: 3,
    children: [
      { id: "node-3-1", name: "Заместитель начальника", personName: "", numericId: 3, role: "Заместитель начальника ОРВ", icon: "Sword", order: 1, children: [] },
      { id: "node-3-2", name: "Заместитель начальника", personName: "", numericId: 4, role: "Заместитель начальника ОРВ", icon: "Zap",   order: 2, children: [] },
    ]
  },
  { id: "node-4", name: "Специалисты", personName: "", numericId: 0, role: "Специалисты", icon: "Shield", order: 4,
    children: [
      { id: "node-5", name: "Ведущий специалист", personName: "", numericId: 5, role: "Ведущий специалист", icon: "Shield", order: 1, children: [] },
      { id: "node-6", name: "Специалист",         personName: "", numericId: 6, role: "Специалист",         icon: "Shield", order: 2, children: [] },
      { id: "node-7", name: "Специалист",         personName: "", numericId: 7, role: "Специалист",         icon: "Shield", order: 3, children: [] },
    ]
  },
];

export const useOrganizationsStore = create<OrganizationsState>((set, get) => ({
  organizations: [],
  currentOrganization: null,
  loading: false,
  error: null,

  init: () => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    set({ loading: true });

    const q = query(collection(db, "organizations"), orderBy("createdAt", "desc"));

    unsubscribe = onSnapshot(q, (snapshot) => {
      const orgs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Organization[];
      set({ organizations: orgs, loading: false });

      const currentUser = useAuthStore.getState().user;
      if (currentUser?.organizationId) {
        const userOrg = orgs.find((o) => o.id === currentUser.organizationId);
        if (userOrg) set({ currentOrganization: userOrg });
      }
    });
  },

  cleanup: () => {
    if (unsubscribe) unsubscribe();
    set({ organizations: [], currentOrganization: null, loading: false, error: null });
  },

  createOrganization: async (name: string, shortName: string, city?: string) => {
    try {
      const orgId = `org-${Date.now()}`;
      const orgRef = doc(db, "organizations", orgId);

      await setDoc(orgRef, {
        id: orgId,
        name: name.trim(),
        shortName: shortName.trim(),
        city: city?.trim() || "",
        active: true,
        createdAt: serverTimestamp(),
        createdBy: useAuthStore.getState().user?.id || "system",
        structure: DEFAULT_STRUCTURE,
      });

      toast({ title: "Организация создана", description: `${shortName} + структура` });
      return orgId;
    } catch (err: any) {
      console.error(err);
      toast({ title: "Ошибка создания организации", variant: "destructive" });
      return null;
    }
  },

  /** Создаёт пользователей на основе переданного списка (из структуры) */
  createDefaultUsers: async (orgId: string, users: any[] = []) => {
    try {
      const usersToCreate = users.length > 0 ? users : []; // если ничего не передали — не создаём

      if (usersToCreate.length === 0) {
        toast({ title: "Нет пользователей для создания", description: "Заполните ФИО в структуре", variant: "destructive" });
        return false;
      }

      console.log(`👤 Создаём ${usersToCreate.length} пользователей для организации ${orgId}`);

      const promises = usersToCreate.map(async (u) => {
        const userRef = await addDoc(collection(db, "users"), {
          numericId: u.numericId || Date.now(),
          login: u.login || u.name.split(" ")[0] || "user",
          password: "143",
          name: u.name,
          role: u.role || "Сотрудник",
          canCreateTask: u.canCreateTask !== false,
          isAdmin: u.isAdmin || false,
          active: true,
          organizationId: orgId,
          createdAt: serverTimestamp(),
          lastLogin: null,
        });
        console.log(`   → Создан пользователь: ${u.name}`);
      });

      await Promise.all(promises);

      toast({
        title: "✅ Пользователи созданы",
        description: `${usersToCreate.length} аккаунтов добавлено`,
      });
      return true;
    } catch (err: any) {
      console.error("Ошибка создания пользователей:", err);
      toast({ title: "Ошибка создания пользователей", variant: "destructive" });
      return false;
    }
  },

  updateOrganization: async (orgId: string, updates: Partial<Organization>) => {
    try {
      await updateDoc(doc(db, "organizations", orgId), updates);
      toast({ title: "Организация обновлена" });
    } catch (err) {
      console.error(err);
      toast({ title: "Ошибка обновления", variant: "destructive" });
    }
  },

  deleteOrganization: async (orgId: string) => {
    if (!confirm("Удалить организацию?")) return;
    try {
      await deleteDoc(doc(db, "organizations", orgId));
      toast({ title: "Организация удалена" });
    } catch (err) {
      toast({ title: "Ошибка удаления", variant: "destructive" });
    }
  },

  updateStructure: async (orgId: string, newStructure: OrgNode[]) => {
    try {
      await updateDoc(doc(db, "organizations", orgId), { structure: newStructure });
      toast({ title: "Структура успешно сохранена" });
    } catch (err) {
      console.error(err);
      toast({ title: "Не удалось сохранить структуру", variant: "destructive" });
    }
  },

  setCurrentOrganization: (org: Organization | null) => set({ currentOrganization: org }),
}));